import { z } from 'zod';
import { AllCharLabelSchema, HumanFullNameSchema, UserTypeSchema } from '../common/schemas';
import { DBPermissionPermissionIdSchema, PermissionValueSchema } from '../permissions/schemas';
import { UserIdSchema } from '../users/schemas';
import { GroupPermissionType, PermissionGroupPrefix, PermissionGroupType } from './enums';

/**
 * Shared & Helper Schemas
 */

export const DBPermissionGroupGenericPermissionsSchema = z.record(
  z.lazy(() => DBPermissionPermissionIdSchema),
  z.object({}).strict()
);

export const PermissionGroupGenericPermissionsSchema = z.array(z.lazy(() => PermissionValueSchema));

export const DBPermissionGroupUsersEntrySchema = z.object({
  name: HumanFullNameSchema,
  type: UserTypeSchema,
});

export const PermissionGroupUsersEntrySchema = z.object({
  name: HumanFullNameSchema,
  type: UserTypeSchema,
  userId: z.lazy(() => UserIdSchema),
});

export const DBGroupPermissionTypeSchema = z.enum(GroupPermissionType);

export const PermissionGroupTypeSchema = z.enum(PermissionGroupType).openapi({
  description: 'Whether the group was made by hand or belongs to a class',
  example: PermissionGroupType.Custom,
});

/** A group's type, which its id's prefix records. */
export const getPermissionGroupType = (groupId: string): PermissionGroupType =>
  groupId.startsWith(PermissionGroupPrefix.Class)
    ? PermissionGroupType.Class
    : PermissionGroupType.Custom;

/**
 * DB Schemas
 */

export const DBPermissionGroupGroupIdSchema = z
  .string()
  .regex(/^[A-Z0-9_]+$/)
  .refine(val => Object.values(PermissionGroupPrefix).some(prefix => val.startsWith(prefix)))
  .min(1)
  .max(256);

export const DBPermissionGroupNameSchema = AllCharLabelSchema.min(1).max(256);
export const DBPermissionGroupBasePermissionsSchema = DBPermissionGroupGenericPermissionsSchema;
export const DBPermissionGroupCurrPermissionsSchema = DBPermissionGroupGenericPermissionsSchema;
export const DBPermissionGroupHideSchema = z.boolean();
export const DBPermissionGroupUsersSchema = z.record(
  z.lazy(() => UserIdSchema),
  DBPermissionGroupUsersEntrySchema
);

export const DBPermissionGroupSchema = z.object({
  groupId: DBPermissionGroupGroupIdSchema,
  // .optional() indicates to ElectroDB that keys of these dynamic fields may be deleted
  basePermissions: DBPermissionGroupBasePermissionsSchema.optional(),
  currPermissions: DBPermissionGroupCurrPermissionsSchema.optional(),
  hide: DBPermissionGroupHideSchema,
  name: DBPermissionGroupNameSchema,
  users: DBPermissionGroupUsersSchema.optional(),
});

/**
 * Client Schemas
 */

export const PermissionGroupGroupIdSchema = DBPermissionGroupGroupIdSchema;
export const PermissionGroupBasePermissionIdsSchema = PermissionGroupGenericPermissionsSchema;
export const PermissionGroupCurrPermissionIdsSchema = PermissionGroupGenericPermissionsSchema;
export const PermissionGroupHideSchema = DBPermissionGroupHideSchema;
export const PermissionGroupLabelSchema = DBPermissionGroupNameSchema;
export const PermissionGroupUsersSchema = z.array(PermissionGroupUsersEntrySchema);

export const PermissionGroupSchema = z.object({
  id: PermissionGroupGroupIdSchema.meta({ title: 'Group ID' }),
  type: PermissionGroupTypeSchema,
  basePermissionIds: PermissionGroupBasePermissionIdsSchema.default([]).meta({
    title: 'Base permissions',
  }),
  currPermissionIds: PermissionGroupCurrPermissionIdsSchema.default([]).meta({
    title: 'Current permissions',
  }),
  hide: PermissionGroupHideSchema.default(false),
  label: PermissionGroupLabelSchema.meta({ title: 'Name' }),
  users: PermissionGroupUsersSchema.default([]).meta({ title: 'Users' }),
});
